import jsPDF from 'jspdf'
import moment from 'moment'
import { getShotFrameUrl } from '../components/ShotFrames/frameUtils'

const normalizeText = value => {
  if (typeof value === 'string') return value.trim()
  return ''
}

const getDialogueText = dialogue => {
  if (!dialogue) return ''
  if (typeof dialogue === 'string') return dialogue.trim()
  if (Array.isArray(dialogue)) {
    return dialogue
      .map(line => {
        if (typeof line === 'string') return line.trim()
        if (line && typeof line === 'object') {
          const speaker = normalizeText(line.speaker || line.character || '')
          const text = normalizeText(
            line.text || line.dialogue || line.line || ''
          )
          return speaker && text ? `${speaker}: ${text}` : text
        }
        return ''
      })
      .filter(Boolean)
      .join('\n')
  }
  if (typeof dialogue === 'object') {
    return Object.entries(dialogue)
      .map(([speaker, text]) =>
        speaker && text ? `${speaker}: ${String(text).trim()}` : ''
      )
      .filter(Boolean)
      .join('\n')
  }
  return ''
}

const getShotImage = shot => {
  return (
    shot?.imageSrc ||
    getShotFrameUrl(shot) ||
    shot?.image_url ||
    shot?.image ||
    shot?.thumbnail_image?.image_url ||
    shot?.thumbnailImage?.image_url ||
    shot?.thumbnailImage?.imageUrl ||
    shot?.thumbnail_image?.url ||
    ''
  )
}

const getShotVideoUrl = shot => {
  return (
    normalizeText(shot?.vidSrc) ||
    normalizeText(shot?.video_url) ||
    normalizeText(shot?.videoUrl) ||
    normalizeText(shot?.url) ||
    ''
  )
}

const getShotCaption = (shot, sceneIndex, shotIndex) => {
  const shotLabel = `Shot ${sceneIndex + 1}.${shotIndex + 1}`
  const caption =
    normalizeText(shot?.title) ||
    normalizeText(shot?.description) ||
    normalizeText(shot?.scenery) ||
    normalizeText(shot?.prompt) ||
    ''
  if (caption) return `${shotLabel}: ${caption}`
  return shotLabel
}

const QUALITY_PRESETS = {
  high: { maxWidth: 1400, jpegQuality: 0.9 },
  medium: { maxWidth: 1100, jpegQuality: 0.78 },
  low: { maxWidth: 850, jpegQuality: 0.62 }
}

const optimizeImageForPdf = async (imageData, quality = 'medium') => {
  if (!imageData || typeof imageData !== 'string') return imageData
  const preset = QUALITY_PRESETS[quality] || QUALITY_PRESETS.medium
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      try {
        const targetWidth = Math.min(
          img.naturalWidth || img.width,
          preset.maxWidth
        )
        const scale =
          targetWidth / (img.naturalWidth || img.width || targetWidth)
        const targetHeight = Math.max(
          1,
          Math.round((img.naturalHeight || img.height || 1) * scale)
        )
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
        resolve(canvas.toDataURL('image/jpeg', preset.jpegQuality))
      } catch (error) {
        resolve(imageData)
      }
    }
    img.onerror = () => resolve(imageData)
    img.src = imageData
  })
}

const getImageFormat = imageData => {
  if (typeof imageData !== 'string') return 'JPEG'
  if (imageData.startsWith('data:image/png')) return 'PNG'
  if (imageData.startsWith('data:image/webp')) return 'WEBP'
  return 'JPEG'
}

const getImageDimensions = imageData =>
  new Promise(resolve => {
    if (!imageData) {
      resolve(null)
      return
    }
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height
      })
    }
    img.onerror = () => resolve(null)
    img.src = imageData
  })

const getSceneHeading = (sceneTitle, sceneIndex) => {
  const trimmedTitle = normalizeText(sceneTitle)
  const sceneLabel = `Scene ${sceneIndex + 1}`
  if (!trimmedTitle) return sceneLabel
  const normalized = trimmedTitle
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
  const expected = sceneLabel.toLowerCase()
  if (normalized === expected || normalized === `scene ${sceneIndex + 1}:`) {
    return sceneLabel
  }
  return `${sceneLabel}: ${trimmedTitle}`
}

const clampLinesWithEllipsis = (lines, maxLines) => {
  if (!Array.isArray(lines) || lines.length <= maxLines) return lines || []
  const clamped = lines.slice(0, maxLines)
  const lastLine = clamped[maxLines - 1] || ''
  clamped[maxLines - 1] = `${lastLine.replace(/\s+$/, '')}...`
  return clamped
}

export const generateStoryboardPdf = async ({
  item,
  fetchVideoById,
  resolveImageDataUrl,
  onProgress,
  quality = 'medium',
  autoDownload = true
}) => {
  const notify = (status, progress) => {
    if (onProgress) onProgress({ status, progress })
  }

  notify('Fetching latest storyboard data...', 5)
  const detailsResponse = await fetchVideoById(item.id)
  const fullVideo = detailsResponse?.data?.data || detailsResponse?.data || item
  const storyText =
    normalizeText(fullVideo.voiceover_text) ||
    normalizeText(fullVideo.voiceoverText) ||
    'Story text is not available.'
  const scenes = Array.isArray(fullVideo.shot_list)
    ? fullVideo.shot_list
    : Array.isArray(fullVideo.shotList)
    ? fullVideo.shotList
    : []

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 30
  const headerHeight = 26
  const footerHeight = 20
  const contentWidth = pageWidth - margin * 2
  const dividerColor = [220, 225, 235]
  const textColor = [0, 0, 0]
  let cursorY = margin + headerHeight
  const totalShots = scenes.reduce(
    (sum, scene) =>
      sum + (Array.isArray(scene?.shots) ? scene.shots.length : 0),
    0
  )
  const progressTotal = Math.max(totalShots + scenes.length + 3, 1)
  let progressStep = 1
  let bodyPageNumber = 0

  const drawPageBranding = (showPageNumber = true) => {
    pdf.setDrawColor(232, 236, 243)
    pdf.line(
      margin,
      pageHeight - footerHeight - 8,
      pageWidth - margin,
      pageHeight - footerHeight - 8
    )
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(120, 128, 144)
    pdf.text('ShareLook', margin, pageHeight - footerHeight + 6)
    if (showPageNumber) {
      bodyPageNumber += 1
      pdf.text(
        `Page ${bodyPageNumber}`,
        pageWidth - margin - 36,
        pageHeight - footerHeight + 6
      )
    }
  }

  const updateProgress = (status, increment = 1) => {
    progressStep += increment
    const nextProgress = Math.min(
      95,
      Math.round((progressStep / progressTotal) * 100)
    )
    notify(status, nextProgress)
  }

  const ensureSpace = neededHeight => {
    if (cursorY + neededHeight > pageHeight - margin - footerHeight) {
      pdf.addPage()
      drawPageBranding(true)
      cursorY = margin + headerHeight
    }
  }

  pdf.setFillColor(255, 255, 255)
  pdf.rect(0, 0, pageWidth, pageHeight, 'F')
  // Cover accents: centered, minimal, premium.
  pdf.setFillColor(245, 248, 255)
  pdf.rect(0, 0, pageWidth, 96, 'F')
  pdf.setFillColor(230, 236, 252)
  pdf.rect(0, pageHeight - 88, pageWidth, 88, 'F')
  pdf.setFillColor(80, 87, 213)
  pdf.circle(pageWidth / 2 - 118, 256, 2, 'F')
  pdf.circle(pageWidth / 2 + 118, 256, 2, 'F')
  pdf.setTextColor(28, 40, 68)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(10)
  pdf.text('SHARELOOK', pageWidth / 2, 142, { align: 'center' })
  pdf.setFontSize(34)
  pdf.text('Video Storyboard', pageWidth / 2, 236, { align: 'center' })
  pdf.setDrawColor(200, 210, 238)
  pdf.line(pageWidth / 2 - 108, 256, pageWidth / 2 + 108, 256)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(98, 109, 138)
  pdf.text('Storyboard Document', pageWidth / 2, 285, { align: 'center' })
  const coverDateY = pageHeight - 92
  pdf.setFontSize(10)
  pdf.setTextColor(95, 106, 129)
  pdf.text(
    `Prepared on ${moment().format('MMMM D, YYYY')}`,
    pageWidth / 2,
    coverDateY,
    {
      align: 'center'
    }
  )
  pdf.setFontSize(9)
  pdf.setTextColor(120, 128, 144)
  pdf.text('ShareLook', margin, pageHeight - footerHeight + 6)
  updateProgress('Preparing storyboard layout...')

  pdf.addPage()
  drawPageBranding(true)
  cursorY = margin + headerHeight

  pdf.setTextColor(...textColor)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(18)
  pdf.text('Story', margin, cursorY)
  cursorY += 12
  pdf.setDrawColor(...dividerColor)
  pdf.line(margin, cursorY, pageWidth - margin, cursorY)
  cursorY += 18
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(11)
  const storyParagraphs = String(storyText || '')
    .split(/\n+/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean)

  const paragraphGap = 9
  for (let pIndex = 0; pIndex < storyParagraphs.length; pIndex += 1) {
    const wrappedParagraph = pdf.splitTextToSize(
      storyParagraphs[pIndex],
      contentWidth
    )
    for (let i = 0; i < wrappedParagraph.length; i += 1) {
      ensureSpace(15)
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(11)
      pdf.setTextColor(0, 0, 0)
      pdf.text(wrappedParagraph[i], margin, cursorY)
      cursorY += 15
    }
    if (pIndex < storyParagraphs.length - 1) {
      ensureSpace(paragraphGap)
      cursorY += paragraphGap
    }
  }
  cursorY += 20
  updateProgress('Adding story section...')

  if (scenes.length > 0) {
    pdf.addPage()
    drawPageBranding(true)
    cursorY = margin + headerHeight
  }

  for (let sceneIndex = 0; sceneIndex < scenes.length; sceneIndex += 1) {
    if (sceneIndex > 0) {
      pdf.addPage()
      drawPageBranding(true)
      cursorY = margin + headerHeight
    }

    const scene = scenes[sceneIndex]
    const sceneTitle =
      normalizeText(scene.title) ||
      normalizeText(scene.id) ||
      `Scene ${sceneIndex + 1}`
    const sceneVoiceover =
      normalizeText(scene.aggregated_voiceover) ||
      normalizeText(scene.voiceover_text)
    const shots = Array.isArray(scene.shots) ? scene.shots : []

    ensureSpace(84)
    pdf.setTextColor(20, 33, 61)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(18)
    pdf.text(getSceneHeading(sceneTitle, sceneIndex), margin, cursorY)
    cursorY += 12
    pdf.setDrawColor(...dividerColor)
    pdf.line(margin, cursorY, pageWidth - margin, cursorY)
    cursorY += 16
    if (sceneVoiceover) {
      ensureSpace(20)
      pdf.setTextColor(20, 33, 61)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(10.5)
      pdf.text('Scene Narration', margin, cursorY)
      cursorY += 14
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10.5)
      pdf.setTextColor(0, 0, 0)
      const sceneVoiceoverText = pdf.splitTextToSize(
        sceneVoiceover,
        contentWidth
      )
      for (let i = 0; i < sceneVoiceoverText.length; i += 1) {
        ensureSpace(14)
        pdf.text(sceneVoiceoverText[i], margin, cursorY)
        cursorY += 14
      }
      cursorY += 8
    }
    cursorY += 8
    updateProgress(`Formatting Scene ${sceneIndex + 1} of ${scenes.length}...`)

    let shotIndex = 0
    while (shotIndex < shots.length) {
      if (shotIndex > 0) {
        pdf.addPage()
        drawPageBranding(true)
        cursorY = margin + headerHeight
        pdf.setTextColor(20, 33, 61)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(14)
        pdf.text(
          `${getSceneHeading(sceneTitle, sceneIndex)} (continued)`,
          margin,
          cursorY
        )
        cursorY += 16
        pdf.setDrawColor(...dividerColor)
        pdf.line(margin, cursorY, pageWidth - margin, cursorY)
        cursorY += 14
      }

      const shotsRemaining = shots.length - shotIndex
      const cardsThisPage = Math.min(2, shotsRemaining)
      const shotGap = 10
      const availableHeight =
        pageHeight -
        margin -
        footerHeight -
        cursorY -
        shotGap * (cardsThisPage - 1)
      const minCardHeight = 210
      const maxCardHeight = 360
      const slotHeight = Math.floor(availableHeight / cardsThisPage)
      const shotCardHeight = Math.max(
        Math.min(slotHeight, maxCardHeight),
        Math.min(minCardHeight, slotHeight)
      )

      for (
        let localCardIndex = 0;
        localCardIndex < cardsThisPage;
        localCardIndex += 1
      ) {
        const currentShotIndex = shotIndex + localCardIndex
        const shot = shots[currentShotIndex]
        const shotDialogue = getDialogueText(shot.dialogue)
        const shotVideoUrl = getShotVideoUrl(shot)
        const shotCaption = getShotCaption(shot, sceneIndex, currentShotIndex)
        const shotImage = getShotImage(shot)
        const imageDataRaw = resolveImageDataUrl
          ? await resolveImageDataUrl(shotImage)
          : null
        const imageData = await optimizeImageForPdf(imageDataRaw, quality)

        const innerPadding = 12
        const shotTitleHeight = 6
        const imageW = contentWidth - innerPadding * 2
        const maxImageH = Math.floor((pageHeight - margin * 2) * 0.5)
        const imageMinH = 140
        const textBlockHeight = 110
        let imageH = Math.max(
          imageMinH,
          Math.min(
            maxImageH,
            shotCardHeight -
              (innerPadding * 2 + shotTitleHeight + textBlockHeight)
          )
        )
        let imageRenderData = null

        if (imageData) {
          const imageDimensions = await getImageDimensions(imageData)
          if (imageDimensions?.width && imageDimensions?.height) {
            const scaleToWidth = imageW / imageDimensions.width
            const computedH = Math.round(imageDimensions.height * scaleToWidth)
            imageH = Math.max(imageMinH, Math.min(maxImageH, computedH))
            imageH = Math.min(imageH, shotCardHeight - 120)
            imageRenderData = {
              imageDimensions,
              imageData
            }
          }
        }

        const textW = imageW
        const wrappedDialogue = clampLinesWithEllipsis(
          pdf.splitTextToSize(
            shotDialogue || 'No dialogue text for this shot.',
            textW
          ),
          4
        )

        pdf.setDrawColor(...dividerColor)
        pdf.rect(margin, cursorY, contentWidth, shotCardHeight)

        const imageX = margin + innerPadding
        const imageY = cursorY + innerPadding + shotTitleHeight + 4
        const captionStripHeight = 24
        notify(
          `Rendering Scene ${sceneIndex + 1}, Shot ${currentShotIndex + 1}...`
        )

        if (imageRenderData?.imageData) {
          try {
            const imageDimensions = imageRenderData.imageDimensions
            if (imageDimensions?.width && imageDimensions?.height) {
              const scale = Math.min(
                imageW / imageDimensions.width,
                imageH / imageDimensions.height
              )
              const renderW = imageDimensions.width * scale
              const renderH = imageDimensions.height * scale
              const renderX = imageX
              const renderY = imageY + (imageH - renderH) / 2
              pdf.addImage(
                imageRenderData.imageData,
                getImageFormat(imageRenderData.imageData),
                renderX,
                renderY,
                renderW,
                renderH
              )
              const overlayH = captionStripHeight
              const overlayY = renderY + renderH
              // Softer ShareLook-tinted strip to reduce visual heaviness.
              pdf.setFillColor(231, 236, 255)
              pdf.rect(renderX, overlayY, renderW, overlayH, 'F')
              pdf.setFont('helvetica', 'bold')
              pdf.setFontSize(9.8)
              pdf.setTextColor(43, 53, 89)
              const wrappedCaption = clampLinesWithEllipsis(
                pdf.splitTextToSize(shotCaption, renderW - 14),
                1
              )
              pdf.text(wrappedCaption, renderX + 7, overlayY + 16)
            } else {
              pdf.addImage(
                imageRenderData.imageData,
                getImageFormat(imageRenderData.imageData),
                imageX,
                imageY,
                imageW,
                imageH
              )
              const overlayH = captionStripHeight
              pdf.setFillColor(231, 236, 255)
              pdf.rect(imageX, imageY + imageH, imageW, overlayH, 'F')
              pdf.setFont('helvetica', 'bold')
              pdf.setFontSize(9.8)
              pdf.setTextColor(43, 53, 89)
              const wrappedCaption = clampLinesWithEllipsis(
                pdf.splitTextToSize(shotCaption, imageW - 14),
                1
              )
              pdf.text(wrappedCaption, imageX + 7, imageY + imageH + 16)
            }
          } catch (err) {
            pdf.setFillColor(245, 245, 245)
            pdf.rect(imageX, imageY, imageW, imageH, 'F')
            pdf.setTextColor(125, 125, 125)
            pdf.setFontSize(10)
            pdf.text('Image preview unavailable', imageX + 20, imageY + 62)
          }
        } else {
          pdf.setFillColor(245, 245, 245)
          pdf.rect(imageX, imageY, imageW, imageH, 'F')
          pdf.setTextColor(125, 125, 125)
          pdf.setFontSize(10)
          pdf.text('Image preview unavailable', imageX + 20, imageY + 62)
        }

        const textX = imageX
        let textY = imageY + imageH + captionStripHeight + 18

        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(10.6)
        pdf.setTextColor(20, 33, 61)
        pdf.text('Dialogue', textX, textY)
        textY += 15
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(10.2)
        pdf.setTextColor(0, 0, 0)
        pdf.text(wrappedDialogue, textX, textY)
        textY += wrappedDialogue.length * 12 + 12

        if (shotVideoUrl) {
          pdf.setFont('helvetica', 'italic')
          pdf.setFontSize(9.8)
          pdf.setTextColor(70, 97, 211)
          const linkLabel = 'Open video shot >>'
          pdf.textWithLink(linkLabel, textX, textY, { url: shotVideoUrl })
        } else {
          pdf.setFont('helvetica', 'italic')
          pdf.setFontSize(9.6)
          pdf.setTextColor(120, 128, 144)
          pdf.text('No video link available', textX, textY)
        }

        cursorY += shotCardHeight
        if (localCardIndex < cardsThisPage - 1) {
          cursorY += shotGap
        }
        updateProgress('Preparing storyboard frames...')
      }
      shotIndex += cardsThisPage
    }
  }

  const safeTitle = `sharelook-storyboard-${item.id ||
    moment().format('YYYYMMDDHHmmss')}`
  notify('Finalizing PDF...', 100)
  const fileName = `${safeTitle}.pdf`
  const blob = pdf.output('blob')

  if (autoDownload) {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return {
    blob,
    fileName,
    format: 'pdf',
    mimeType: 'application/pdf'
  }
}
